/**
 * Create or edit one narration voice. Mount it only while open (the page keys it per voice) so the
 * form is seeded once and a background refetch cannot wipe what is being typed.
 *
 * The voice settings are optional: blank sends null, which means "use the ElevenLabs default".
 */

import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { LibraryMusic, ManageSearch } from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { narrationApi } from '@api/endpoints/narration'
import type { AdminNarrationVoiceDto, ElevenLabsVoiceInfo } from '@api/types/narration'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { ElevenLabsVoicePicker } from './ElevenLabsVoicePicker'
import {
  NARRATION_LANGUAGES,
  VOICE_DESCRIPTION_MAX,
  VOICE_NUMBER_RANGES,
  type VoiceDraft,
  type VoiceNumberField,
  applyElevenLabsVoice,
  blankVoiceDraft,
  elevenLabsListError,
  elevenLabsVoicesKey,
  isValidElevenLabsVoiceId,
  isVoiceNotInAccountError,
  looksLikeElevenLabsVoiceId,
  languageLabel,
  narrationErrorMessage,
  narrationVoicesKey,
  normalizeLanguageTag,
  validateVoiceDraft,
  voiceDraftFromDto,
  voiceRequestFromDraft,
} from './narrationForm'
import { PreviewButton } from './PreviewButton'
import { usePreviewAudio } from './usePreviewAudio'

const NUMBER_FIELDS: { key: VoiceNumberField; label: string; helper: string }[] = [
  { key: 'stability', label: 'Stability', helper: 'Lower is more expressive, higher more even.' },
  { key: 'similarityBoost', label: 'Similarity boost', helper: 'How closely to stick to the original voice.' },
  { key: 'style', label: 'Style', helper: 'Exaggerates the speaking style. 0 is neutral.' },
  { key: 'speed', label: 'Speed', helper: '1 is normal pace.' },
]

export function NarrationVoiceDialog({
  voice,
  nextSortOrder,
  existing,
  onClose,
}: {
  voice: AdminNarrationVoiceDto | null
  nextSortOrder: number
  existing: readonly AdminNarrationVoiceDto[]
  onClose: () => void
}) {
  const creating = voice === null
  const dialogProps = useMobileDialogProps()
  const queryClient = useQueryClient()
  const audio = usePreviewAudio()
  const [draft, setDraft] = useState<VoiceDraft>(() => (voice ? voiceDraftFromDto(voice) : blankVoiceDraft(nextSortOrder)))
  const [attempted, setAttempted] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  /** What ElevenLabs said about the id in the field (from Look up, the picker or Add to account). */
  const [found, setFound] = useState<ElevenLabsVoiceInfo | null>(null)
  /** The last id looked up automatically, so a blur after a paste does not ask twice. */
  const lastLookup = useRef<string>(voice?.elevenLabsVoiceId ?? '')

  const errors = useMemo(() => validateVoiceDraft(draft), [draft])
  const shown = attempted ? errors : {}
  const existingIds = useMemo(() => new Set(existing.map((v) => v.elevenLabsVoiceId)), [existing])
  const duplicate = existing.find((v) => v.elevenLabsVoiceId === draft.elevenLabsVoiceId.trim() && v.id !== voice?.id)

  const save = useMutation({
    mutationFn: () => {
      const request = voiceRequestFromDraft(draft)
      return voice ? narrationApi.voices.update(voice.id, request) : narrationApi.voices.create(request)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: narrationVoicesKey })
      close()
    },
  })

  const lookup = useMutation({
    mutationFn: (voiceId: string) => narrationApi.elevenLabsVoices.get(voiceId),
    onSuccess: (v) => {
      setFound(v)
      setDraft((d) => applyElevenLabsVoice(d, v))
    },
  })

  const addToAccount = useMutation({
    mutationFn: () =>
      narrationApi.elevenLabsVoices.add(draft.elevenLabsVoiceId.trim(), {
        publicOwnerId: found?.voiceId === draft.elevenLabsVoiceId.trim() ? found.publicOwnerId : null,
        name: draft.displayName.trim() || found?.name || null,
      }),
    onSuccess: (v) => {
      setFound({ ...v, isInAccount: true })
      lastLookup.current = v.voiceId
      setDraft((d) => ({ ...d, elevenLabsVoiceId: v.voiceId }))
      save.reset()
      queryClient.invalidateQueries({ queryKey: elevenLabsVoicesKey })
    },
  })

  const runLookup = (raw: string, auto: boolean) => {
    const id = raw.trim()
    if (!isValidElevenLabsVoiceId(id)) return
    if (auto && (!looksLikeElevenLabsVoiceId(id) || id === lastLookup.current)) return
    lastLookup.current = id
    lookup.mutate(id)
  }

  const currentId = draft.elevenLabsVoiceId.trim()
  const foundHere = found && found.voiceId === currentId ? found : null
  const notInAccount = (foundHere != null && foundHere.isInAccount === false) || isVoiceNotInAccountError(save.error)
  const lookupError = lookup.error
    ? (lookup.error as { response?: { status?: number } }).response?.status === 404
      ? 'ElevenLabs has no voice with that id.'
      : elevenLabsListError(lookup.error).title
    : null

  const close = () => {
    audio.stop()
    onClose()
  }

  const submit = () => {
    setAttempted(true)
    if (Object.keys(errors).length > 0) return
    save.mutate()
  }

  const set = <K extends keyof VoiceDraft>(key: K, value: VoiceDraft[K]) => setDraft((d) => ({ ...d, [key]: value }))

  const text = (key: Exclude<keyof VoiceDraft, 'languages' | 'isActive'>) => ({
    value: draft[key],
    onChange: (e: ChangeEvent<HTMLInputElement>) => set(key, e.target.value),
    error: Boolean(shown[key]),
    size: 'small' as const,
    fullWidth: true,
  })

  const previewUrl = draft.previewUrl.trim()

  return (
    <Dialog open onClose={save.isPending ? undefined : close} maxWidth="sm" fullWidth {...dialogProps}>
      <DialogTitle>{creating ? 'New narration voice' : `Edit ${voice.displayName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Button variant="outlined" startIcon={<LibraryMusic />} onClick={() => setPickerOpen(true)}>
              Pick from ElevenLabs
            </Button>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Search your voices or the ElevenLabs voice library. Or paste a voice id below and it fills the rest.
            </Typography>
          </Box>

          <TextField
            label="ElevenLabs voice id"
            {...text('elevenLabsVoiceId')}
            onBlur={(e) => runLookup(e.target.value, true)}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData('text')
              // The field takes the paste itself; look up what it will hold when the paste is the whole id.
              if (looksLikeElevenLabsVoiceId(pasted)) runLookup(pasted, true)
            }}
            required
            inputProps={{ spellCheck: false, autoCapitalize: 'off', style: { fontFamily: 'monospace' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    startIcon={<ManageSearch fontSize="small" />}
                    disabled={!isValidElevenLabsVoiceId(draft.elevenLabsVoiceId) || lookup.isPending}
                    onClick={() => runLookup(draft.elevenLabsVoiceId, false)}
                  >
                    {lookup.isPending ? 'Looking…' : 'Look up'}
                  </Button>
                </InputAdornment>
              ),
            }}
            helperText={
              shown.elevenLabsVoiceId ??
              (duplicate ? `"${duplicate.displayName}" already uses this ElevenLabs voice.` : 'Creators never see it.')
            }
          />

          {lookupError && !lookup.isPending && <Alert severity="error">{lookupError}</Alert>}
          {notInAccount ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" disabled={addToAccount.isPending} onClick={() => addToAccount.mutate()}>
                  {addToAccount.isPending ? 'Adding…' : 'Add to account'}
                </Button>
              }
            >
              This voice is in the ElevenLabs library but not in the account, so narration can't use it yet.
              {addToAccount.error && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  {narrationErrorMessage(addToAccount.error, 'The voice could not be added.')}
                </Typography>
              )}
            </Alert>
          ) : (
            foundHere &&
            !lookup.isPending && (
              <Alert severity="success">
                Filled from ElevenLabs: {foundHere.name}
                {foundHere.isInAccount ? ' (in the account).' : '.'}
              </Alert>
            )
          )}
          <TextField
            label="Display name"
            {...text('displayName')}
            required
            helperText={shown.displayName ?? 'What creators pick from.'}
          />

          <Autocomplete
            multiple
            freeSolo
            options={NARRATION_LANGUAGES.map((l) => l.code)}
            value={draft.languages}
            onChange={(_, value) => {
              // Normalise typed tags as they are added; keep anything invalid so it can be shown and removed.
              const next: string[] = []
              for (const raw of value) {
                const tag = normalizeLanguageTag(raw) ?? raw.trim()
                if (tag && !next.some((t) => t.toLowerCase() === tag.toLowerCase())) next.push(tag)
              }
              set('languages', next)
            }}
            getOptionLabel={(code) => languageLabel(code)}
            renderOption={(props, code) => (
              <li {...props} key={code}>
                {languageLabel(code)}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {code}
                </Typography>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((code, index) => {
                // getTagProps may carry its own key; ours is the code, which is unique in the list.
                const { key: _ignored, ...tagProps } = getTagProps({ index }) as ReturnType<typeof getTagProps> & {
                  key?: unknown
                }
                void _ignored
                return (
                  <Chip
                    key={code}
                    size="small"
                    label={code}
                    color={normalizeLanguageTag(code) ? 'default' : 'error'}
                    {...tagProps}
                  />
                )
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Languages"
                size="small"
                placeholder={draft.languages.length === 0 ? 'Any language' : undefined}
                error={Boolean(shown.languages)}
                helperText={
                  shown.languages ??
                  'Empty means the voice is offered for any language. Type other BCP-47 tags (ja, pt-BR) and press Enter.'
                }
              />
            )}
          />

          <TextField
            label="Description"
            {...text('description')}
            multiline
            minRows={2}
            helperText={shown.description ?? `Shown to creators. Up to ${VOICE_DESCRIPTION_MAX} characters.`}
          />

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Preview URL"
              {...text('previewUrl')}
              inputProps={{ spellCheck: false, autoCapitalize: 'off' }}
              helperText={shown.previewUrl ?? 'An https MP3 creators can audition.'}
            />
            <Box sx={{ pt: 0.5 }}>
              <PreviewButton
                url={previewUrl.startsWith('https://') ? previewUrl : null}
                playing={audio.playing}
                failed={audio.failed}
                onToggle={audio.toggle}
                label={draft.displayName || 'this voice'}
              />
            </Box>
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Voice settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Optional. Leave blank to use the ElevenLabs default.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {NUMBER_FIELDS.map(({ key, label, helper }) => {
                const { min, max } = VOICE_NUMBER_RANGES[key]
                return (
                  <TextField
                    key={key}
                    label={`${label} (${min}–${max})`}
                    {...text(key)}
                    type="number"
                    placeholder="Default"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min, max, step: 0.05 }}
                    helperText={shown[key] ?? helper}
                  />
                )
              })}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <Box>
              <FormControlLabel
                control={<Switch checked={draft.isActive} onChange={(e) => set('isActive', e.target.checked)} />}
                label="Active"
              />
              <Typography variant="caption" color="text.secondary" component="div">
                Inactive voices are hidden from creators; existing narrations keep their audio.
              </Typography>
            </Box>
            <TextField
              label="Sort order"
              {...text('sortOrder')}
              type="number"
              helperText={shown.sortOrder ?? 'Lower numbers are listed first.'}
            />
          </Box>

          {attempted && Object.keys(errors).length > 0 && (
            <Alert severity="warning">Fix the highlighted fields to save.</Alert>
          )}
          {save.error && !isVoiceNotInAccountError(save.error) && <Alert severity="error">{narrationErrorMessage(save.error, 'The voice could not be saved')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : creating ? 'Add voice' : 'Save'}
        </Button>
      </DialogActions>

      <ElevenLabsVoicePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        existingIds={existingIds}
        onPick={(picked) => {
          setDraft((d) => applyElevenLabsVoice(d, picked))
          setFound(picked)
          lastLookup.current = picked.voiceId
          setPickerOpen(false)
        }}
      />
    </Dialog>
  )
}
